import React, { useEffect, useState } from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import { formStyles } from '../../styles/forms';
import { isValidUrl, normalizeUrl } from '../../utils/validationUrl';
import {
  ControlledTextInput,
  ControlledTextInputProps,
} from './controlledTextInput';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type AddLinkProps = {
  name: string;
  control: Control<any>;
  debounceMs?: number;
  label?: string;
  controlledTextInputProps?: Omit<ControlledTextInputProps, 'name' | 'control'>;
};

export const AddLink: React.FC<AddLinkProps> = ({
  name,
  control,
  debounceMs = 600,
  label = 'routines.admin.linkUrl',
  controlledTextInputProps,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState('');

  const { field } = useController({ name, control });
  const url = useWatch({ control, name });

  useEffect(() => {
    if (!url?.trim()) {
      setError('');
      return;
    }

    const handler = setTimeout(() => {
      const normalized = normalizeUrl(url);
      if (!isValidUrl(normalized)) {
        setError(t('routines.admin.invalidUrl') || 'Invalid URL');
      } else {
        setError('');
        if (normalized !== url) {
          field.onChange(normalized);
        }
      }
    }, debounceMs);

    return () => clearTimeout(handler);
  }, [url, debounceMs, field, t]);

  return (
    <View style={styles.formItem}>
      <Text style={formStyles.formItemTitle}>{t(label)}</Text>

      <ControlledTextInput
        control={control}
        name={name}
        placeholder={t('routines.admin.linkUrlPlaceholder')}
        onFocus={() => setError('')}
        {...controlledTextInputProps}
        hideBorder
      />

      {!!error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  formItem: {
    marginBottom: 12,
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
    color: role.textPrimary,
  },
  errorText: {
    color: 'red',
    marginTop: 4,
    fontSize: 12,
  },
}));
